import React, { useEffect, useState } from 'react'

type Props = {
  src: string
  posterSrc?: string
  className?: string
  alt?: string
  /** Reports the thumbnail's intrinsic width/height ratio once known. */
  onAspect?: (ratio: number) => void
}

// Old iOS (< 16.4) has no WebCodecs, so every card falls back to native
// <video> frame capture, but iOS caps how many videos can decode at once
// (effectively one). Five cards capturing in parallel mostly fail. This gate
// runs native captures ONE AT A TIME, project-wide, so each gets a decoder.
// Modern browsers don't hit this path (Mediabunny handles them concurrently).
let nativeThumbQueue: Promise<unknown> = Promise.resolve()
const queueNativeCapture = <T,>(task: () => Promise<T>): Promise<T> => {
  const run = nativeThumbQueue.then(task, task)
  nativeThumbQueue = run.then(
    () => undefined,
    () => undefined,
  )
  return run
}

// Renders a still-frame thumbnail from a video URL. Prefers a CMS-provided
// poster (posterSrc) — no video download at all. Falls back to decoding the
// first frame via Mediabunny's UrlSource, which uses HTTP range requests to
// read ONLY the bytes needed for frame 0 (not the whole file). Also sidesteps
// iOS Safari restrictions on hidden <video> first-frame paint and canvas
// tainting from cross-subdomain fetches.
const VideoThumb = ({ src, posterSrc, className, alt = '', onAspect }: Props) => {
  const [thumbUrl, setThumbUrl] = useState<string | null>(posterSrc ?? null)

  useEffect(() => {
    // CMS poster set → use it directly, never touch the video file.
    if (posterSrc) {
      setThumbUrl(posterSrc)
      return
    }

    let cancelled = false
    let canvasUrl: string | null = null
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let input: any = null

    // Native <video> first-frame capture. Fallback for browsers without
    // WebCodecs (iOS < 16.4, older Android/desktop) where Mediabunny can't
    // decode. iOS won't paint a detached or un-played video, so the element is
    // attached (offscreen) and played muted, then the first painted frame is
    // drawn to a canvas. Works only when S3 CORS is clean (crossOrigin
    // anonymous) so the canvas isn't tainted; otherwise resolves null and we
    // keep the placeholder. Run via queueNativeCapture so only one decodes at a
    // time (iOS single-decoder limit).
    const captureNative = (): Promise<string | null> =>
      new Promise(resolve => {
        const v = document.createElement('video')
        v.crossOrigin = 'anonymous'
        v.preload = 'auto'
        v.muted = true
        // some iOS builds only honor muted autoplay with the attribute set too
        v.defaultMuted = true
        v.playsInline = true
        v.setAttribute('muted', '')
        v.setAttribute('playsinline', '')
        // iOS only plays a video that is actually on-screen (offscreen/hidden
        // videos never start, so no frame ever decodes). Park a tiny, nearly
        // invisible element in a corner of the viewport so playback is allowed,
        // then remove it once the frame is captured.
        v.style.cssText =
          'position:fixed;left:0;bottom:0;width:8px;height:8px;opacity:0.02;z-index:-1;pointer-events:none'
        let done = false
        let grabbed = false
        const cleanup = () => {
          try {
            v.pause()
          } catch {
            /* ignore */
          }
          v.removeAttribute('src')
          try {
            v.load()
          } catch {
            /* ignore */
          }
          if (v.parentNode) v.parentNode.removeChild(v)
        }
        const finish = (out: string | null) => {
          if (done) return
          done = true
          cleanup()
          resolve(out)
        }
        const grab = () => {
          if (grabbed) return
          grabbed = true
          try {
            const w = 320
            const ratio =
              v.videoWidth && v.videoHeight ? v.videoHeight / v.videoWidth : 16 / 9
            const h = Math.round(w * ratio)
            const canvas = document.createElement('canvas')
            canvas.width = w
            canvas.height = h
            const ctx = canvas.getContext('2d')
            if (!ctx) return finish(null)
            ctx.drawImage(v, 0, 0, w, h)
            canvas.toBlob(
              b => finish(b ? URL.createObjectURL(b) : null),
              'image/jpeg',
              0.75,
            )
          } catch {
            // tainted canvas (CORS) or draw error, give up gracefully
            finish(null)
          }
        }
        // requestVideoFrameCallback (Safari 15.4+) fires on an actual paint;
        // older iOS relies on the timeupdate/loadeddata listeners.
        const rvfc =
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (v as any).requestVideoFrameCallback as
            | ((cb: () => void) => void)
            | undefined
        // iOS ignores `preload` and won't fetch ANY video data (no
        // loadedmetadata/loadeddata ever fires) until play() is called. So kick
        // play() eagerly instead of waiting for a load event that never comes.
        const kickPlay = () => {
          const p = v.play()
          if (p && typeof p.then === 'function') {
            // muted inline autoplay blocked, seek to force a frame instead
            p.then(undefined, () => {
              try {
                v.currentTime = 0.1
              } catch {
                grab()
              }
            })
          }
        }
        v.addEventListener('loadeddata', () => {
          if (typeof rvfc === 'function') rvfc.call(v, () => grab())
        })
        // first advance past 0 means a frame decoded, capture it
        v.addEventListener('timeupdate', () => {
          if (v.currentTime > 0) grab()
        })
        v.addEventListener('seeked', () => {
          if (v.currentTime > 0) grab()
        })
        v.addEventListener('error', () => finish(null))
        // safety timeout, never hang on a stuck load
        setTimeout(() => finish(null), 7000)
        document.body.appendChild(v)
        v.src = src
        v.load()
        kickPlay()
      })

    const run = async () => {
      try {
        const { ALL_FORMATS, UrlSource, Input, VideoSampleSink } = await import(
          'mediabunny'
        )

        // UrlSource streams via range requests — only the moov atom + the
        // first sample chunk are fetched, not the entire MP4.
        input = new Input({
          // cache: 'reload' bypasses any opaque/non-CORS cache entry a prior
          // no-cors <video> load wrote for this URL — reading it from a cors
          // request fails with "No 'Access-Control-Allow-Origin' header present".
          source: new UrlSource(src, { requestInit: { cache: 'reload' } }),
          formats: ALL_FORMATS,
        })
        const track = await input.getPrimaryVideoTrack()
        if (!track || cancelled) return
        // displayWidth/Height are post-rotation, the real on-screen dims
        const dw = track.displayWidth || 720
        const dh = track.displayHeight || 1280
        const sink = new VideoSampleSink(track)
        const sample = await sink.getSample(0)
        if (!sample || cancelled) return

        const w = 320
        const h = Math.max(1, Math.round((w * dh) / dw))
        const canvas = document.createElement('canvas')
        canvas.width = w
        canvas.height = h
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          sample.close()
          return
        }
        sample.drawWithFit(ctx, { fit: 'cover' })
        sample.close()

        canvas.toBlob(
          b => {
            if (!b || cancelled) return
            canvasUrl = URL.createObjectURL(b)
            setThumbUrl(canvasUrl)
          },
          'image/jpeg',
          0.75
        )
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[video-poc] mediabunny thumb failed, trying native', src, e)
        // serialize native captures, iOS decodes ~one video at a time
        const nativeUrl = await queueNativeCapture(() => captureNative())
        if (cancelled) {
          if (nativeUrl) URL.revokeObjectURL(nativeUrl)
          return
        }
        if (nativeUrl) {
          canvasUrl = nativeUrl
          setThumbUrl(nativeUrl)
        }
      }
    }
    run()

    return () => {
      cancelled = true
      input?.dispose?.()
      if (canvasUrl) URL.revokeObjectURL(canvasUrl)
    }
  }, [src, posterSrc])

  if (thumbUrl) {
    return (
      <img
        src={thumbUrl}
        alt={alt}
        className={className}
        draggable={false}
        onLoad={e => {
          const img = e.currentTarget
          if (img.naturalWidth && img.naturalHeight) {
            onAspect?.(img.naturalWidth / img.naturalHeight)
          }
        }}
      />
    )
  }
  return <div className={className} style={{ background: '#1a1a1a' }} />
}

export default VideoThumb
